import { gsap } from 'gsap';

const HERO_IMAGES = ['/hero_bg.jpg', '/hero_bg_1.jpg', '/hero_bg_2.jpg', '/hero_bg_3.jpg'];
const ROTATE_INTERVAL_MS = 10000;
const CROSSFADE_DURATION = 1.6;

export type HeroBackgroundRotationOptions = {
  onImageChange?: (imageUrl: string) => void;
};

/**
 * Rotates hero section background images every 10s with a smooth crossfade.
 * Fades the liquid canvas out during transition, then updates it and fades back in.
 */
export class HeroBackgroundRotation {
  private layerA: HTMLElement | null = null;
  private layerB: HTMLElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private onImageChange: ((imageUrl: string) => void) | null = null;
  private currentIndex = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private isTransitioning = false;

  constructor(options: HeroBackgroundRotationOptions = {}) {
    const container = document.querySelector('.scroll-hero-bg');
    if (!container) return;

    this.layerA = container.querySelector('.hero-bg-layer--a');
    this.layerB = container.querySelector('.hero-bg-layer--b');
    this.canvas = document.getElementById('hero-liquid-canvas') as HTMLCanvasElement | null;
    this.onImageChange = options.onImageChange ?? null;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion || !this.layerA || !this.layerB) return;

    this.setLayerBackground(this.layerA, HERO_IMAGES[0]);
    this.setLayerBackground(this.layerB, HERO_IMAGES[1]);
    gsap.set(this.layerB, { opacity: 0 });

    this.intervalId = setInterval(() => this.runTransition(), ROTATE_INTERVAL_MS);
  }

  private setLayerBackground(el: HTMLElement, url: string): void {
    el.style.backgroundImage = `url(${url})`;
  }

  private runTransition(): void {
    const layerA = this.layerA;
    const layerB = this.layerB;
    const canvas = this.canvas;
    if (!layerA || !layerB || this.isTransitioning) return;

    this.isTransitioning = true;
    const nextIndex = (this.currentIndex + 1) % HERO_IMAGES.length;
    const nextUrl = HERO_IMAGES[nextIndex];

    this.setLayerBackground(layerB, nextUrl);

    const crossfadeStart = 0.25;
    const tl = gsap.timeline({
      onComplete: () => {
        this.currentIndex = nextIndex;
        this.isTransitioning = false;
        if (this.layerA) this.setLayerBackground(this.layerA, nextUrl);
        if (this.layerB) this.setLayerBackground(this.layerB, HERO_IMAGES[(nextIndex + 1) % HERO_IMAGES.length]);
        gsap.set(layerA, { opacity: 1 });
        gsap.set(layerB, { opacity: 0 });
      },
    });

    if (canvas) tl.to(canvas, { opacity: 0, duration: 0.4, ease: 'power2.in' }, 0);
    tl.to(layerA, { opacity: 0, duration: CROSSFADE_DURATION, ease: 'power2.inOut' }, crossfadeStart);
    tl.to(layerB, { opacity: 1, duration: CROSSFADE_DURATION, ease: 'power2.inOut' }, crossfadeStart);
    tl.add(() => {
      this.onImageChange?.(nextUrl);
    }, crossfadeStart + CROSSFADE_DURATION);
    if (canvas) tl.to(canvas, { opacity: 1, duration: 0.5, ease: 'power2.out' }, crossfadeStart + CROSSFADE_DURATION + 0.05);
  }

  dispose(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.layerA = null;
    this.layerB = null;
    this.canvas = null;
    this.onImageChange = null;
  }
}
